import { inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { isSameDay, startOfDay } from 'date-fns';
import { combineLatest } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { getEncounterViewDefaultDate, isClosedEncounterStay } from '../../infrastructure/helpers';
import {
    getCurrentEncounterProp,
    getEncounterStartDate,
    getEncounterViewSelectedDate,
    isReviewMode,
} from '../selectors';

/**
 * Sets Encounter View Selected Date
 * @returns function
 */
export const setEncounterViewSelectedDate: () => (selectedDate: Date) => void = () => {
    const store = inject(Store);

    return (selectedDate: Date) => {
        store.dispatch({ type: 'ENCOUNTER:SET_SELECTED_DATE', selectedDate });
    };
};

/** set selected date for discharged / review encounters - effect is primary; this is a safety net */
export const setEncounterSelectedDateInReviewMode = () => {
    const store = inject(Store);
    const isReviewmode$ = isReviewMode();
    const encounterStartDate$ = getEncounterStartDate(true);
    const encounterEndDate$ = getCurrentEncounterProp('endDate');
    const currentSelectedDate$ = getEncounterViewSelectedDate();

    return () => {
        combineLatest([isReviewmode$, encounterStartDate$, encounterEndDate$, currentSelectedDate$])
            .pipe(
                // Wait until stay is known closed (endDate and/or review mode), not first emission
                filter(([reviewMode, , endDate]) => isClosedEncounterStay(endDate, reviewMode)),
                take(1),
            )
            .subscribe(([, encounterStartDate, endDate, currentSelectedDate]) => {
                const targetDate = getEncounterViewDefaultDate(encounterStartDate, endDate, true);
                if (!currentSelectedDate || !isSameDay(currentSelectedDate, startOfDay(targetDate))) {
                    store.dispatch({ type: 'ENCOUNTER:SET_SELECTED_DATE', selectedDate: targetDate });
                }
            });
    };
};

/** reset encounter selected date: admission day for closed stays, today for active */
export const resetEncounterViewSelectedDate = () => {
    const store = inject(Store);
    const isReviewmode$ = isReviewMode();
    const encounterStartDate$ = getEncounterStartDate(true);
    const encounterEndDate$ = getCurrentEncounterProp('endDate');
    const encounterActive$ = getCurrentEncounterProp('active');
    const currentSelectedDate$ = getEncounterViewSelectedDate();

    return () => {
        combineLatest([isReviewmode$, encounterStartDate$, encounterEndDate$, encounterActive$, currentSelectedDate$])
            .pipe(
                // Wait until stay is classifiable — avoid take(1) while reviewMode/endDate still unset (web login race)
                filter(
                    ([reviewMode, , endDate, active]) => isClosedEncounterStay(endDate, reviewMode) || active === true,
                ),
                take(1),
                map(([reviewMode, encounterStartDate, endDate, , currentSelectedDate]) => {
                    const date = getEncounterViewDefaultDate(encounterStartDate, endDate, reviewMode);
                    const normalizedDate = startOfDay(date);
                    if (!currentSelectedDate || !isSameDay(currentSelectedDate, normalizedDate)) {
                        return date;
                    }
                    return null; // Don't dispatch if date is already correct
                }),
                filter(date => date !== null),
            )
            .subscribe(selectedDate => {
                store.dispatch({ type: 'ENCOUNTER:SET_SELECTED_DATE', selectedDate });
            });
    };
};
